import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { supabase, type Notice, type NoticeAttachment } from '../../lib/supabase';

const ITEMS_PER_PAGE = 15;

export default function NoticePage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [noticeList, setNoticeList] = useState<(Notice & { files: NoticeAttachment[] })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Set page title and meta tags
  useEffect(() => {
    document.title = '미래인재학원 공지사항 - 학원 소식';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '미래인재학원의 최신 공지사항을 확인하세요. 입시 정보, 학원 일정, 특강 안내 등 중요한 소식을 실시간으로 제공합니다.');
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', '미래인재학원 공지사항 - 입시 정보 및 학원 소식');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', '미래인재학원의 최신 공지사항을 확인하세요. 입시 정보, 학원 일정, 특강 안내 등 중요한 소식을 실시간으로 제공합니다.');
    }
  }, []);

  // 공지사항 목록 불러오기
  useEffect(() => {
    const fetchNotices = async () => {
      setIsLoading(true);
      try {
        const { data: notices, error: noticesError } = await supabase
          .from('notices')
          .select('*')
          .order('created_at', { ascending: false });

        if (noticesError) throw noticesError;

        const noticesWithFiles = await Promise.all(
          (notices || []).map(async (notice) => {
            const { data: attachments } = await supabase
              .from('notice_attachments')
              .select('*')
              .eq('notice_id', notice.id)
              .order('display_order', { ascending: true });

            return { ...notice, files: attachments || [] };
          })
        );

        setNoticeList(noticesWithFiles);
      } catch (error) {
        console.error('Failed to fetch notices:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const filteredNotices = useMemo(() => {
    let result = [...noticeList];

    if (activeSearch.trim()) {
      const q = activeSearch.trim().toLowerCase();
      result = result.filter((n) => n.title.toLowerCase().includes(q));
    }

    return result;
  }, [noticeList, activeSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredNotices.length / ITEMS_PER_PAGE));
  const paginatedNotices = filteredNotices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const pinnedNotices = noticeList.filter((n) => n.is_pinned);

  const handleSearch = () => {
    setActiveSearch(searchQuery);
    setCurrentPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation currentPage="notice" />

      {/* Hero */}
      <section className="pt-20 relative min-h-[200px] md:min-h-[320px] flex items-center">
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(251,191,36,0.12)_0%,transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(251,191,36,0.08)_0%,transparent_40%)]"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16 w-full">
          <div className="max-w-2xl">
            <span className="text-yellow-400 font-semibold text-xs md:text-sm tracking-wider uppercase">Notice</span>
            <h1 className="text-2xl md:text-5xl font-bold text-white mt-2 md:mt-4 mb-2 md:mb-4">미래인재학원 공지사항</h1>
            <p className="text-sm md:text-lg text-gray-300 leading-relaxed">
              미래인재학원의 최신 소식과 안내사항을 확인하세요
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-6 md:py-12 px-3 md:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Top bar */}
          <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
            <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">
              Total <strong className="text-gray-900">{filteredNotices.length}</strong>건
            </span>

            <div className="flex items-center gap-1.5 md:gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="제목 검색"
                className="h-8 md:h-10 px-3 md:px-4 border border-gray-300 rounded-lg text-xs md:text-sm w-32 md:w-48 focus:outline-none focus:border-yellow-400"
              />
              <button
                onClick={handleSearch}
                className="h-8 md:h-10 px-3 md:px-5 bg-gray-900 text-white text-xs md:text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                검색
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="border-t-2 border-gray-900">
            {/* Header */}
            <div className="grid grid-cols-[36px_1fr_56px_40px] md:grid-cols-[80px_1fr_100px_80px] bg-gray-50 border-b border-gray-200">
              <div className="py-2 md:py-3 px-1 md:px-4 text-center text-[10px] md:text-xs font-bold text-gray-500">번호</div>
              <div className="py-2 md:py-3 px-1 md:px-4 text-[10px] md:text-xs font-bold text-gray-500">제목</div>
              <div className="py-2 md:py-3 px-1 md:px-4 text-center text-[10px] md:text-xs font-bold text-gray-500">날짜</div>
              <div className="py-2 md:py-3 px-1 md:px-4 text-center text-[10px] md:text-xs font-bold text-gray-500">조회</div>
            </div>

            {isLoading ? (
              <div className="py-20 text-center">
                <i className="ri-loader-4-line text-4xl text-gray-300 mb-3 block animate-spin"></i>
                <p className="text-gray-400 text-sm">불러오는 중...</p>
              </div>
            ) : (
              <>
                {/* Pinned notices */}
                {!activeSearch && pinnedNotices.map((notice) => (
                  <div
                    key={`pinned-${notice.id}`}
                    onClick={() => navigate(`/notice/${notice.id}`)}
                    className="grid grid-cols-[36px_1fr_56px_40px] md:grid-cols-[80px_1fr_100px_80px] border-b border-gray-200 bg-yellow-50/50 hover:bg-yellow-50 transition-colors cursor-pointer"
                  >
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center justify-center">
                      <span className="w-5 h-5 md:w-7 md:h-7 bg-yellow-400 rounded-md flex items-center justify-center">
                        <i className="ri-pushpin-line text-black text-[10px] md:text-sm"></i>
                      </span>
                    </div>
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center gap-1 min-w-0">
                      <span className="text-xs md:text-sm font-semibold text-gray-900 hover:text-yellow-600 transition-colors truncate">
                        {notice.title}
                      </span>
                      {notice.files.length > 0 && (
                        <i className="ri-attachment-2 text-[10px] md:text-xs text-gray-400 flex-shrink-0"></i>
                      )}
                    </div>
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center justify-center">
                      <span className="text-[10px] md:text-xs text-gray-500">{formatDate(notice.created_at)}</span>
                    </div>
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center justify-center">
                      <span className="text-[10px] md:text-xs text-gray-500">{notice.views.toLocaleString()}</span>
                    </div>
                  </div>
                ))}

                {/* Regular notices */}
                {paginatedNotices.map((notice, index) => (
                  <div
                    key={notice.id}
                    onClick={() => navigate(`/notice/${notice.id}`)}
                    className="grid grid-cols-[36px_1fr_56px_40px] md:grid-cols-[80px_1fr_100px_80px] border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center justify-center">
                      <span className="text-[10px] md:text-sm text-gray-400 font-medium">{filteredNotices.length - ((currentPage - 1) * ITEMS_PER_PAGE + index)}</span>
                    </div>
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center gap-1 min-w-0">
                      <span className="text-xs md:text-sm text-gray-800 hover:text-yellow-600 transition-colors truncate">
                        {notice.title}
                      </span>
                      {notice.files.length > 0 && (
                        <i className="ri-attachment-2 text-[10px] md:text-xs text-gray-400 flex-shrink-0"></i>
                      )}
                    </div>
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center justify-center">
                      <span className="text-[10px] md:text-xs text-gray-500">{formatDate(notice.created_at)}</span>
                    </div>
                    <div className="py-3 md:py-4 px-1 md:px-4 flex items-center justify-center">
                      <span className="text-[10px] md:text-xs text-gray-500">{notice.views.toLocaleString()}</span>
                    </div>
                  </div>
                ))}

                {paginatedNotices.length === 0 && !isLoading && (
                  <div className="py-20 text-center">
                    <i className="ri-file-search-line text-4xl text-gray-300 mb-3 block"></i>
                    <p className="text-gray-400 text-sm">검색 결과가 없습니다</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && filteredNotices.length > 0 && (
            <div className="flex items-center justify-center gap-0.5 md:gap-1 mt-6 md:mt-8">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-skip-back-mini-line"></i>
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>

              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    currentPage === page
                      ? 'bg-yellow-400 text-black font-bold'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-skip-forward-mini-line"></i>
              </button>
            </div>
          )}

          {/* 관리자 로그인 */}
          <div className="flex justify-end mt-4 md:mt-6">
            <Link
              to="/notice/admin"
              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all cursor-pointer"
              title="관리자 로그인"
            >
              <i className="ri-lock-line text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
